import ConversationMarker from "@/domain/entities/vistassistant/ConversationMarker";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cardClassName } from "./assistantClasses";

/**
 * Where the conversation was compacted: a separator, and what the assistant keeps in mind (the
 * summary written by the Api-Ai), folded.
 */
const CompactionMarker = ({ marker }: { marker: ConversationMarker }) => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const summaryId = useId();
    const time = marker.at.toLocaleTimeString(i18n.language, {
        hour: "2-digit",
        minute: "2-digit",
    });
    const label = t("vistassistant.compaction.marker", { time });

    return (
        <div className="flex w-full animate-opacity flex-col gap-2.5">
            <div
                aria-label={label}
                className="flex items-center gap-3 text-neutral-dark"
                role="separator"
            >
                <span className="h-px flex-1 bg-assistant-line" />
                <span className="text-xs font-medium">{label}</span>
                <span className="h-px flex-1 bg-assistant-line" />
            </div>
            {!!marker.summary && (
                <section
                    className={cn(
                        cardClassName,
                        "flex flex-col gap-3 rounded-[20px] px-5 py-4",
                    )}
                >
                    <button
                        aria-controls={summaryId}
                        aria-expanded={isOpen}
                        className="flex w-full items-center gap-3 text-left text-lg font-semibold text-primary-darkest"
                        onClick={() => setIsOpen((open) => !open)}
                        type="button"
                    >
                        <ChevronDown
                            aria-hidden
                            className={cn(
                                "size-5 flex-none text-primary-dark transition-transform",
                                isOpen && "rotate-180",
                            )}
                            strokeWidth={1.75}
                        />
                        {t("vistassistant.compaction.summaryTitle")}
                    </button>
                    <p
                        className="whitespace-pre-line text-base leading-relaxed text-primary-darkest"
                        hidden={!isOpen}
                        id={summaryId}
                    >
                        {marker.summary}
                    </p>
                </section>
            )}
        </div>
    );
};

export default CompactionMarker;
